import React, { useState, Component } from 'react';
import { ArrowLeftIcon, CalendarIcon, StickyNoteIcon, PhoneIcon, MailIcon, MapPinIcon, EditIcon, HashIcon, CopyIcon, CheckIcon, ListTodoIcon, UserPlusIcon, PlusIcon } from 'lucide-react';
import { Customer } from '../components/CustomerCard';
import { Task, TaskCard } from '../components/TaskCard';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
interface CustomerDetailProps {
  customer: Customer;
  tasks: Task[];
  onBack: () => void;
  onEdit: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onAddTask: (customerId: string) => void;
}
export function CustomerDetail({
  customer,
  tasks,
  onBack,
  onEdit,
  onEditTask,
  onDeleteTask,
  onAddTask
}: CustomerDetailProps) {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedSecondaryPhone, setCopiedSecondaryPhone] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | Task['status']>('all');
  const copyToClipboard = async (text: string, type: 'phone' | 'address' | 'secondaryPhone') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else if (type === 'address') {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 2000);
      } else if (type === 'secondaryPhone') {
        setCopiedSecondaryPhone(true);
        setTimeout(() => setCopiedSecondaryPhone(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const customerTasks = tasks.filter(t => t.customerId === customer.id);
  const activeTasks = customerTasks.filter(t => t.status === 'in-progress').length;
  const completedTasks = customerTasks.filter(t => t.status === 'done').length;
  const plannedTasks = customerTasks.filter(t => t.status === 'planned').length;
  const pendingTasks = customerTasks.filter(t => t.status === 'pending').length;
  const filteredTasks = selectedStatus === 'all' ? customerTasks : customerTasks.filter(t => t.status === selectedStatus);
  return <div data-id="element-1478">
      <div className="flex items-center gap-4 mb-8" data-id="element-1479">
        <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Tilbage" data-id="element-1480">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-1481" />
        </button>
        <div className="flex-1" data-id="element-1482">
          <h1 className="text-4xl font-bold text-stone-900" data-id="element-1483">{customer.name}</h1>
          <div className="flex items-center gap-2 mt-2" data-id="element-1484">
            <HashIcon className="w-4 h-4 text-teal-600" data-id="element-1485" />
            <p className="text-teal-600 font-medium" data-id="element-1486">{customer.customerId}</p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-1487">
          <Button onClick={() => onAddTask(customer.id)} variant="primary" className="flex items-center gap-2" data-id="element-1488">
            <PlusIcon className="w-4 h-4" data-id="element-1489" />
            Tilføj opgave
          </Button>
          <Button onClick={() => onEdit(customer)} variant="secondary" className="flex items-center gap-2" data-id="element-1490">
            <EditIcon className="w-4 h-4" data-id="element-1491" />
            Rediger
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8" data-id="element-1492">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1493">
          <div className="flex items-center gap-3 mb-2" data-id="element-1494">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1495">
              <CalendarIcon className="w-5 h-5 text-blue-700" data-id="element-1496" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1497">
              Opgaver i alt
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1498">
            {customerTasks.length}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.05
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1499">
          <div className="flex items-center gap-3 mb-2" data-id="element-1500">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1501">
              <CalendarIcon className="w-5 h-5 text-amber-700" data-id="element-1502" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1503">
              Aktive opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1504">{activeTasks}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1505">
          <div className="flex items-center gap-3 mb-2" data-id="element-1506">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1507">
              <CalendarIcon className="w-5 h-5 text-emerald-700" data-id="element-1508" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1509">
              Afsluttede opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1510">{completedTasks}</p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.15
    }} className="bg-white rounded-xl p-8 shadow-sm border border-stone-100" data-id="element-1511">
        <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1512">
          Kundeinformation
        </h2>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-1513">
          {customer.address && <div className="flex items-start gap-4" data-id="element-1514">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1515">
                <MapPinIcon className="w-5 h-5 text-teal-700" data-id="element-1516" />
              </div>
              <div className="flex-1" data-id="element-1517">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1518">
                  Adresse
                </p>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`} target="_blank" rel="noopener noreferrer" className="text-stone-900 hover:text-teal-600 transition-colors underline" data-id="element-1519">
                  {customer.address}
                </a>
              </div>
              <button onClick={() => copyToClipboard(customer.address, 'address')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér adresse" data-id="element-1520">
                {copiedAddress ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1521" /> : <CopyIcon className="w-5 h-5" data-id="element-1522" />}
              </button>
            </div>}

          {customer.phone && <div className="flex items-start gap-4" data-id="element-1523">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1524">
                <PhoneIcon className="w-5 h-5 text-teal-700" data-id="element-1525" />
              </div>
              <div className="flex-1" data-id="element-1526">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1527">
                  Telefon
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1528">{customer.phone}</p>
              </div>
              <button onClick={() => copyToClipboard(customer.phone, 'phone')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1529">
                {copiedPhone ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1530" /> : <CopyIcon className="w-5 h-5" data-id="element-1531" />}
              </button>
            </div>}

          {customer.email && <div className="flex items-start gap-4" data-id="element-1532">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1533">
                <MailIcon className="w-5 h-5 text-teal-700" data-id="element-1534" />
              </div>
              <div className="flex-1" data-id="element-1535">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1536">Email</p>
                <a href={`mailto:${customer.email}`} className="text-stone-900 hover:text-teal-600 transition-colors" data-id="element-1537">
                  {customer.email}
                </a>
              </div>
            </div>}

          {customer.secondaryContactName && <div className="flex items-start gap-4" data-id="element-1538">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1539">
                <UserPlusIcon className="w-5 h-5 text-teal-700" data-id="element-1540" />
              </div>
              <div className="flex-1" data-id="element-1541">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1542">
                  Ekstra kontaktperson
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1543">
                  {customer.secondaryContactName}
                </p>
                {customer.secondaryContactPhone && <div className="flex items-center gap-2 mt-1" data-id="element-1544">
                    <p className="text-stone-600 text-sm" data-id="element-1545">
                      {customer.secondaryContactPhone}
                    </p>
                    <button onClick={() => copyToClipboard(customer.secondaryContactPhone!, 'secondaryPhone')} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1546">
                      {copiedSecondaryPhone ? <CheckIcon className="w-3 h-3 text-teal-600" data-id="element-1547" /> : <CopyIcon className="w-3 h-3" data-id="element-1548" />}
                    </button>
                  </div>}
              </div>
            </div>}
        </div>

        {customer.notes && <div className="mt-6 pt-6 border-t border-stone-200" data-id="element-1549">
            <div className="flex items-start gap-4" data-id="element-1550">
              <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1551">
                <StickyNoteIcon className="w-5 h-5 text-amber-700" data-id="element-1552" />
              </div>
              <div className="flex-1" data-id="element-1553">
                <p className="text-sm font-medium text-stone-500 mb-2" data-id="element-1554">Noter</p>
                <p className="text-stone-700 leading-relaxed" data-id="element-1555">
                  {customer.notes}
                </p>
              </div>
            </div>
          </div>}
      </motion.div>

      {/* Customer Tasks Section */}
      {customerTasks.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.2
    }} className="mt-8" data-id="element-1556">
          <div className="flex items-center justify-between mb-6" data-id="element-1557">
            <div className="flex items-center gap-3" data-id="element-1558">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1559">
                <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1560" />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1561">
                Opgaver for {customer.name}
              </h2>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-1562">
            <button onClick={() => setSelectedStatus('all')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1563">
              Alle ({customerTasks.length})
            </button>
            <button onClick={() => setSelectedStatus('in-progress')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1564">
              I gang ({activeTasks})
            </button>
            <button onClick={() => setSelectedStatus('done')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1565">
              Afsluttet ({completedTasks})
            </button>
            <button onClick={() => setSelectedStatus('planned')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1566">
              Planlagt ({plannedTasks})
            </button>
            <button onClick={() => setSelectedStatus('pending')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1567">
              Afventende ({pendingTasks})
            </button>
          </div>

          {/* Tasks Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1568">
            {filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} data-id="element-1569" />)}
          </div>

          {filteredTasks.length === 0 && <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1570">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" data-id="element-1571" />
              <p className="text-stone-600 font-medium" data-id="element-1572">
                Ingen opgaver i denne kategori
              </p>
              <p className="text-sm text-stone-500 mt-1" data-id="element-1573">
                Prøv at vælge en anden status
              </p>
            </div>}
        </motion.div>}
    </div>;
}